// Fiyat Tanımları — paylaşılan UI bileşenleriyle yeniden tasarlandı.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import DataTable from '../components/DataTable';
import { TabIcon, TAB_ICONS } from '../utils/tabIcons';
import { Button, Modal, FormField, ListPageHeader, FilterBar } from '../components/ui';
import { CardHero, CardStatusBadge, CardTabs, CardSplitButton } from '../components/card';

const empty = {
  code: '', name: '', price_group: '',
  currency_code: 'TRY',
  is_active: true, is_preferred: false,
  target_account_type: '', target_account_id: '', target_account_group_id: '', target_account_special_code: '',
  is_time_limited: false, valid_from: '', valid_to: '',
  sort_order: 0,
};

function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('tr-TR');
}

export default function PriceDefinitionsPage() {
  const [list, setList] = useState([]);
  const [groups, setGroups] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [specialCodes, setSpecialCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [filters, setFilters] = useState({ price_group: '', target_account_type: '', is_active: '', is_preferred: '' });

  const [modalMode, setModalMode] = useState(null); // 'add' | 'edit'
  const [activeTab, setActiveTab] = useState('detail');
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ ...empty });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const [l, lk, accs, sc] = await Promise.all([
        api.get('/price-definitions', { params }),
        api.get('/price-definitions/lookups'),
        api.get('/accounts', { params: { limit: 500 } }).catch(() => ({ data: { data: { items: [] } } })),
        api.get('/accounts/special-codes').catch(() => ({ data: { data: [] } })),
      ]);
      if (l.data.success) setList(l.data.data || []);
      if (lk.data.success) {
        setGroups(lk.data.data.price_groups || []);
        setAccountTypes(lk.data.data.account_types || []);
      }
      if (accs.data.success) setAccounts((accs.data.data.items || accs.data.data || []).filter(a => a.is_active !== false));
      if (sc.data.success) setSpecialCodes(sc.data.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openAdd = () => { setForm({ ...empty }); setSelected(null); setActiveTab('detail'); setMsg(null); setModalMode('add'); };
  const openEdit = async (row) => {
    try {
      const r = await api.get('/price-definitions/' + row.id);
      if (r.data.success) {
        const d = r.data.data;
        setForm({
          code: d.code || '',
          name: d.name || '',
          price_group: d.price_group || '',
          currency_code: d.currency_code || 'TRY',
          is_active: d.is_active !== false,
          is_preferred: !!d.is_preferred,
          target_account_type: d.target_account_type || '',
          target_account_id: d.target_account_id || '',
          target_account_group_id: d.target_account_group_id || '',
          target_account_special_code: d.target_account_special_code || '',
          is_time_limited: !!d.is_time_limited,
          valid_from: d.valid_from ? d.valid_from.substring(0, 10) : '',
          valid_to: d.valid_to ? d.valid_to.substring(0, 10) : '',
          sort_order: d.sort_order || 0,
        });
        setSelected(d);
        setActiveTab('detail'); setMsg(null);
        setModalMode('edit');
      }
    } catch (e) { console.error(e); }
  };

  const closeModal = () => { setModalMode(null); setSelected(null); setMsg(null); };
  const tf = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = async (mode = 'close') => {
    if (mode && typeof mode === 'object') mode = 'close';
    if (!form.name.trim()) return setMsg({ type: 'error', text: 'Fiyat açıklaması zorunludur' });
    setSaving(true); setMsg(null);
    try {
      const body = {
        ...form,
        target_account_id: form.target_account_id || null,
        target_account_group_id: form.target_account_group_id || null,
        valid_from: form.is_time_limited ? form.valid_from || null : null,
        valid_to:   form.is_time_limited ? form.valid_to   || null : null,
      };
      if (modalMode === 'edit') await api.put('/price-definitions/' + selected.id, body);
      else await api.post('/price-definitions', body);
      await fetchAll();
      if (mode === 'close') closeModal();
      else if (mode === 'new') {
        setSelected(null); setForm({ name: '', currency_code: 'TRY', price_group: '', is_preferred: false });
        setModalMode('add'); setMsg(null);
      }
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Kaydedilemedi' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selected) return;
    if (!window.confirm(`"${selected.name}" fiyat tanımı silinecek. Devam edilsin mi?`)) return;
    try {
      await api.delete('/price-definitions/' + selected.id);
      closeModal();
      fetchAll();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Silinemedi' });
    }
  };

  const filtered = list.filter(r =>
    !search ||
    (r.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (r.price_group || '').toLowerCase().includes(search.toLowerCase()) ||
    (r.code || '').toLowerCase().includes(search.toLowerCase())
  );

  const groupLabel = (code) => groups.find(g => g.code === code)?.label || code || '—';
  const acctTypeLabel = (code) => accountTypes.find(a => a.code === code)?.label || code || '—';

  // Modal aksiyonları — footer'a taşındı (yeni Modal API)
  // PriceDefinitions her iki modda da düzenlenebilir form, edit modunda Sil aktif

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Fiyat Tanımları"
        count={filtered.length}
        unit="kayıt"
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Açıklama, grup, kod ara..."
        onRefresh={fetchAll}
        refreshing={loading}
        actions={<Button size="sm" onClick={openAdd}>+ Yeni Fiyat Tanımı</Button>}
      />

      <FilterBar
        show={showFilterBar}
        onToggle={() => setShowFilterBar(s => !s)}
        values={filters}
        onChange={setFilters}
        fields={[
          { key: 'price_group', type: 'select', label: 'Grup',
            options: [{ value: '', label: 'Tümü' }, ...groups.map(g => ({ value: g.code, label: g.label }))] },
          { key: 'target_account_type', type: 'select', label: 'Hedef Cari Tipi', flex: '1 1 200px',
            options: [{ value: '', label: 'Tümü' }, ...accountTypes.map(a => ({ value: a.code, label: a.label }))] },
          { key: 'is_active', type: 'select', label: 'Durum',
            options: [{ value: '', label: 'Tümü' }, { value: 'yes', label: 'Aktif' }, { value: 'no', label: 'Pasif' }] },
          { key: 'is_preferred', type: 'select', label: 'Tercih Edilen',
            options: [{ value: '', label: 'Tümü' }, { value: 'yes', label: 'Evet' }, { value: 'no', label: 'Hayır' }] },
        ]}
      />

      <div className="stocks-table-wrap">
        <DataTable
          pageKey="price-definitions-list"
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          emptyText='Tanımlı fiyat yok — "+ Yeni Fiyat Tanımı" ile başlayın'
          data={filtered}
          rowKey="id"
          onRowClick={r => openEdit(r)}
          columns={[
            { id: 'name', header: 'Fiyat Açıklama', accessor: 'name', sortable: true,
              cell: r => (<div><div style={{ fontWeight: 600 }}>{r.name}</div>{r.code && <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>{r.code}</div>}</div>) },
            { id: 'group', header: 'Fiyat Grubu', accessor: r => groupLabel(r.price_group), sortable: true, width: 130 },
            { id: 'currency', header: 'Para Birimi', accessor: 'currency_code', sortable: true, width: 100 },
            { id: 'target', header: 'Hedef Cari Tipi', accessor: r => acctTypeLabel(r.target_account_type) + (r.target_account_title ? ` — ${r.target_account_title}` : ''), sortable: true,
              cell: r => <span className="stock-secondary">{acctTypeLabel(r.target_account_type)}{r.target_account_title ? ` — ${r.target_account_title}` : ''}</span> },
            { id: 'valid', header: 'Süreli', accessor: r => r.is_time_limited ? `${fmtDate(r.valid_from)} → ${fmtDate(r.valid_to)}` : '', sortable: true, width: 180,
              cell: r => <span className="stock-secondary">{r.is_time_limited ? `${fmtDate(r.valid_from)} → ${fmtDate(r.valid_to)}` : '—'}</span> },
            { id: 'is_active', header: 'Aktif', accessor: r => r.is_active ? 'Aktif' : 'Pasif', sortable: true, width: 90,
              headerStyle: { textAlign: 'center' }, cellStyle: { textAlign: 'center' },
              cell: r => <span className={'stock-badge ' + (r.is_active ? 'active' : 'passive')}>{r.is_active ? 'Aktif' : 'Pasif'}</span> },
            { id: 'preferred', header: 'Tercih', accessor: r => r.is_preferred ? 1 : 0, sortable: true, width: 70,
              headerStyle: { textAlign: 'center' }, cellStyle: { textAlign: 'center' },
              cell: r => r.is_preferred ? '★' : '' },
          ]}
        />
      </div>

      {modalMode !== null && (
        <Modal
          icon={null}
          title={modalMode === 'add' ? 'Yeni Fiyat Tanımı' : 'Fiyat Tanımı'}
          size="lg"
          onClose={closeModal}
          footer={
            <>
              <div className="ui-modal-footer-left">
                {modalMode === 'edit' && <Button variant="danger" onClick={handleDelete}>🗑 Sil</Button>}
              </div>
              <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Button variant="ghost" onClick={closeModal}>{modalMode === 'add' ? 'Vazgeç' : 'İptal'}</Button>
                <CardSplitButton
                  primaryLabel="💾 Kaydet"
                  primaryAction={() => handleSave('close')}
                  loading={saving}
                  options={[
                    { label: 'Kaydet ve Yeni Aç', icon: '✚', onClick: () => handleSave('new') },
                    { label: 'Kaydet ve Kapat',  icon: '✓', onClick: () => handleSave('close') },
                  ]}
                />
              </div>
            </>
          }
        >
          {modalMode === 'edit' && (
            <CardHero
              icon="💰"
              code={form.code || form.price_group}
              title={selected?.name || form.name}
              subtitle={[form.price_group, form.currency_code || 'TRY'].filter(Boolean).join(' · ')}
              statusBadge={form.is_preferred ? <CardStatusBadge label="★ Tercih Edilen" color="#f59e0b" bg="rgba(245,158,11,0.15)" /> : null}
            />
          )}
          {msg && (
            <div style={{
              marginBottom: 12, padding: '9px 12px', borderRadius: 8, fontSize: '0.84rem',
              background: msg.type === 'error' ? 'var(--danger-bg)' : 'rgba(16,185,129,0.1)',
              color: msg.type === 'error' ? 'var(--danger)' : '#0d9488',
              border: '1px solid ' + (msg.type === 'error' ? 'rgba(239,68,68,0.25)' : 'rgba(16,185,129,0.25)'),
            }}>{msg.text}</div>
          )}

          <CardTabs
            items={[
              { key: 'detail', label: 'Detay',                icon: <TabIcon name="general"/> },
              { key: 'target', label: 'Cari ve Geçerlilik',   icon: <TabIcon name="users"/> },
            ]}
            active={activeTab}
            onChange={setActiveTab}
          />

          {activeTab === 'detail' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              <FormField label="Fiyat Açıklama" required fullWidth>
                <input value={form.name} onChange={e => tf('name', e.target.value)} placeholder="örn. Perakende Fiyat" maxLength={100}/>
              </FormField>
              <FormField label="Kod">
                <input value={form.code} onChange={e => tf('code', e.target.value)} placeholder="opsiyonel" maxLength={20}/>
              </FormField>
              <FormField label="Sıra">
                <input type="number" value={form.sort_order} onChange={e => tf('sort_order', parseInt(e.target.value) || 0)}/>
              </FormField>
              <FormField label="Fiyat Grubu">
                <select value={form.price_group} onChange={e => tf('price_group', e.target.value)}>
                  <option value="">— Seçiniz —</option>
                  {groups.map(g => <option key={g.code} value={g.code}>{g.label}</option>)}
                </select>
              </FormField>
              <FormField label="Para Birimi">
                <select value={form.currency_code} onChange={e => tf('currency_code', e.target.value)}>
                  <option value="TRY">TRY (TL)</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="CHF">CHF</option>
                  <option value="SAR">SAR</option>
                  <option value="AED">AED</option>
                </select>
              </FormField>
              <FormField label="Aktif">
                <select value={form.is_active ? '1' : '0'} onChange={e => tf('is_active', e.target.value === '1')}>
                  <option value="1">Aktif</option>
                  <option value="0">Pasif</option>
                </select>
              </FormField>
              <FormField label="Tercih (★)">
                <select value={form.is_preferred ? '1' : '0'} onChange={e => tf('is_preferred', e.target.value === '1')}>
                  <option value="0">Hayır</option>
                  <option value="1">Tercih (grup içinde varsayılan)</option>
                </select>
              </FormField>
            </div>
          )}

          {activeTab === 'target' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              <div style={{ gridColumn: '1/-1', paddingBottom: 8, borderBottom: '1px dashed var(--border-secondary)', fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                Cari Hedefleme — bu fiyat tanımı hangi carilere otomatik uygulansın?
              </div>
              <FormField label="Cari Tipi">
                <select value={form.target_account_type} onChange={e => tf('target_account_type', e.target.value)}>
                  <option value="">— Tümü —</option>
                  {accountTypes.map(a => <option key={a.code} value={a.code}>{a.label}</option>)}
                </select>
              </FormField>
              <FormField label="Cari Özel Kodu">
                <select value={form.target_account_special_code} onChange={e => tf('target_account_special_code', e.target.value)}>
                  <option value="">— Tümü —</option>
                  {specialCodes.map(s => <option key={s.id} value={s.code || s.name}>{s.code ? s.code + ' - ' : ''}{s.name}</option>)}
                </select>
              </FormField>
              <FormField label="Belirli Cari (opsiyonel — sadece bu cariye uygulansın)" fullWidth>
                <select value={form.target_account_id} onChange={e => tf('target_account_id', e.target.value)}>
                  <option value="">— Tümü —</option>
                  {accounts.slice(0, 200).map(a => <option key={a.id} value={a.id}>{a.code} — {a.title}</option>)}
                </select>
              </FormField>

              <div style={{ gridColumn: '1/-1', marginTop: 8, paddingTop: 12, borderTop: '1px dashed var(--border-secondary)', fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                Geçerlilik
              </div>
              <FormField fullWidth>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.is_time_limited} onChange={e => tf('is_time_limited', e.target.checked)}/>
                  Süreli Fiyat (belirli bir tarih aralığında geçerli)
                </label>
              </FormField>
              {form.is_time_limited && (
                <>
                  <FormField label="Başlangıç Tarihi">
                    <input type="date" value={form.valid_from} onChange={e => tf('valid_from', e.target.value)}/>
                  </FormField>
                  <FormField label="Bitiş Tarihi">
                    <input type="date" value={form.valid_to} onChange={e => tf('valid_to', e.target.value)}/>
                  </FormField>
                </>
              )}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
